import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { apiHandler } from '../../../api/apihandler';
import { GROUP_LIST, GROUP_DELETE } from '../../../api/constants';
import CreateNewGroup from '../../../components/chat/createNewGroup';
import { getAvatarColor, getInitials } from '../feed/feedUtils';
import PeopleYouMayKnowWidget from '../../../components/nemonto/PeopleYouMayKnowWidget';
import MyStatsWidget from '../../../components/nemonto/MyStatsWidget';
import PromoCard from '../../../components/nemonto/PromoCard';
import EmptyState from '../../../components/nemonto/EmptyState';
import '../social/social-list-page.css';

const GroupAvatar = ({ photo, name }) => {
  if (photo) return <img src={photo} alt={name} className="nmsl-avatar" />;
  return (
    <div className="nmsl-avatar" style={{ background: getAvatarColor(name || 'Group') }}>
      {getInitials(...(name || 'Group').split(' '))}
    </div>
  );
};

const GroupsListPage = () => {
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getGroupListApi();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function getGroupListApi() {
    setLoading(true);
    try {
      const res = await apiHandler(GROUP_LIST, 'POST', { page: 1, limit: 100 });
      if (res.data?.response?.status === 200) {
        setGroups(res?.data?.data?.groups?.data || []);
      } else {
        setGroups([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }

  const confirmDelete = (groupId, groupName) => {
    Swal.fire({
      title: 'Delete group?',
      text: `"${groupName}" will be permanently deleted for everyone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
    }).then((result) => {
      if (result.isConfirmed) deleteGroupApi(groupId);
    });
  };

  async function deleteGroupApi(groupId) {
    try {
      const res = await apiHandler(GROUP_DELETE, 'DELETE', { group_id: groupId });
      if (res.data?.response?.status === 200) {
        getGroupListApi();
      } else {
        Swal.fire('Error', res?.response?.data?.data?.message || 'Unable to delete group.', 'error');
      }
    } catch (error) {
      console.error(error);
    }
  }

  return (
    <div className="nmsl-layout">
      <div className="nmsl-main">
        <div className="nmsl-page-header">
          <div className="nmsl-header">
            <h3 className="nmsl-title">Groups</h3>
            {groups.length > 0 && <span className="nmsl-count-pill">{groups.length}</span>}
          </div>
          <p className="nmsl-subtitle">Cook together in shared group chats.</p>
        </div>

        <div className="nmsl-main-body">
          <CreateNewGroup getGroupListApi={getGroupListApi} />

          <div style={{ marginTop: 20 }}>
            {loading && <p className="nmsl-loading">Loading groups…</p>}

            {!loading && groups.length === 0 && (
              <EmptyState icon="diversity_3" title="No groups yet" sub="Create one above to start cooking together." />
            )}

            {!loading && groups.length > 0 && (
              <div className="nmsl-grid">
                {groups.map((item) => (
                  <div
                    key={item.id}
                    className="nmsl-card nmsl-clickable"
                    onClick={() => navigate(`/groups/${item.group_id}`, { state: { group: item } })}
                  >
                    <GroupAvatar photo={item?.user_group?.image} name={item?.user_group?.name} />
                    <div className="nmsl-body">
                      <div className="nmsl-name">
                        {item?.user_group?.name}
                        {item?.is_owner === 1 && <span className="nmsl-badge">Owner</span>}
                      </div>
                    </div>
                    <div className="nmsl-actions">
                      <button
                        type="button"
                        className="nmsl-icon-btn"
                        title="Open chat"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/chat/group/${item.group_id}`, { state: { header: { title: item?.user_group?.name, avatarUrl: item?.user_group?.image, isGroup: true } } });
                        }}
                      >
                        <span className="material-symbols-outlined" style={{ fontSize: 16 }}>chat</span>
                      </button>
                      {item?.is_owner === 1 && (
                        <button
                          type="button"
                          className="nmsl-icon-btn"
                          title="Delete group"
                          onClick={(e) => {
                            e.stopPropagation();
                            confirmDelete(item.group_id, item?.user_group?.name);
                          }}
                        >
                          <span className="material-symbols-outlined" style={{ fontSize: 16 }}>delete</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <aside className="nmsl-rail">
        <PromoCard
          label="Challenges"
          title="Cook it together, compete together"
          sub="Rally your group into a live recipe challenge and see who nails it."
          ctaText="Browse challenges"
          to="/dashboards/app/dancecorner-challenges"
        />
        <PeopleYouMayKnowWidget />
        <MyStatsWidget />
      </aside>
    </div>
  );
};

export default GroupsListPage;
